import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { FaTable } from "react-icons/fa";
import ToggleOverlay from "./ToggleOverlay";
import "../styles/InfoBox.css";

const InfoBox = ({
  selectedFeature,
  featureType,
  onClose,
  isOpen,
  isExpanded,
  onExpandChange
}) => {
  const [isCollapsed, setIsCollapsed] = useState(!isExpanded);
  const boxRef = useRef(null);

  // Update collapsed state when isExpanded prop changes
  React.useEffect(() => {
    setIsCollapsed(!isExpanded);
  }, [isExpanded]);

  // Debug what's being passed to the component
  React.useEffect(() => {
    if (selectedFeature && featureType) {
      console.log("InfoBox received feature:", featureType, selectedFeature);
      console.log("Properties:", selectedFeature.properties);
    }
  }, [selectedFeature, featureType]);

  if (!selectedFeature) return null;

  const properties = selectedFeature.properties || {};

  // Get the area in hectares based on feature type
  const getAreaInHectares = () => {
    // For districts, use Shape__Area field
    if (featureType === "District" && properties.Shape__Area) {
      return Number(properties.Shape__Area) / 10000;
    }

    // For oil palm concessions, use area_ha field
    if (featureType === "Oil Palm Concession" && properties.area_ha) {
      return Number(properties.area_ha);
    }

    // For Sarawak license/LPF, use area_offic field
    if (featureType === "License for Planted Forest" && properties.area_offic) {
      return Number(properties.area_offic);
    }

    // General fallbacks
    if (properties.area_ha) {
      return Number(properties.area_ha);
    }

    if (properties.area_offic) {
      return Number(properties.area_offic);
    }

    if (properties.Shape__Area) {
      return Number(properties.Shape__Area) / 10000;
    }

    if (featureType === "Division" && properties.area) {
      return Number(properties.area);
    }

    if (selectedFeature.areaSqKm) {
      return selectedFeature.areaSqKm * 100; // 1 sq km = 100 hectares
    }

    return null;
  };

  const areaInHectares = getAreaInHectares();

  const handleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    if (onExpandChange) {
      onExpandChange(!newCollapsed);
    }
  };

  // IMPORTANT - Check if this is the default info display or a feature
  const isDefaultInfo = featureType === "Properties" && properties.name === "Map Information";

  // For default info panel (general map info)
  const DefaultInfoContent = () => (
    <div className="info-box-content">
      <div className="info-item">
        <span className="info-box__label">Name:</span>
        <span className="info-box__value">{properties.name || "N/A"}</span>
      </div>

      {properties.description && (
        <div className="info-item">
          <span className="info-box__label">Description:</span>
          <span className="info-box__value">{properties.description}</span>
        </div>
      )}

      {properties.instructions && (
        <div className="info-item">
          <span className="info-box__label">Instructions:</span>
          <span className="info-box__value">{properties.instructions}</span>
        </div>
      )}
    </div>
  );

  // For actual map features (logging concessions, districts, etc.)
  const FeatureInfoContent = () => (
    <div className="info-box-content">
      <div className="info-item">
        <span className="info-box__label">Name:</span>
        <span className="info-box__value">
          {featureType === "Division" ? properties.Division : properties.name || properties.NAME || "N/A"}
        </span>
      </div>

      {(featureType === "Logging Concession" || featureType === "Oil Palm Concession") && properties.company && (
        <div className="info-item">
          <span className="info-box__label">Group/Affiliation:</span>
          <span className="info-box__value">{properties.company}</span>
        </div>
      )}

      {featureType === "License for Planted Forest" && properties.name_of_gr && (
        <div className="info-item">
          <span className="info-box__label">Group:</span>
          <span className="info-box__value">{properties.name_of_gr}</span>
        </div>
      )}

      {areaInHectares !== null && (
        <div className="info-item">
          <span className="info-box__label">Size:</span>
          <span className="info-box__value">
            {areaInHectares.toLocaleString(undefined, { maximumFractionDigits: 2 })} ha
          </span>
        </div>
      )}

      {((featureType === "License for Planted Forest" && properties.source_of) ||
        ((featureType === "Logging Concession" || featureType === "Oil Palm Concession") && properties.source)) && (
        <div className="info-item">
          <span className="info-box__label">Source:</span>
          <span className="info-box__value">
            {properties.source_of || properties.source}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className="info-box" ref={boxRef}>
      <ToggleOverlay
        title={featureType || "Feature Information"}
        icon={FaTable}
        isCollapsed={isCollapsed}
        onCollapse={handleCollapse}
        onClose={onClose}
        className="info-box-dropdown"
        width={400}
      >
        {isDefaultInfo ? <DefaultInfoContent /> : <FeatureInfoContent />}
      </ToggleOverlay>
    </div>
  );
};

InfoBox.propTypes = {
  selectedFeature: PropTypes.object,
  featureType: PropTypes.string,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onExpandChange: PropTypes.func
};

InfoBox.defaultProps = {
  isOpen: true,
  isExpanded: true
};

export default InfoBox;
